import { useFormik } from 'formik';
import React, { Dispatch, SetStateAction, useEffect } from 'react';
import Label from '../../xnp/common/form/Label';
import { ERROR_CLASS } from '../../xnp/common/constants';
import { shouldHaveFormikError } from '../../xnp/common/util';
import classNames from 'classnames';
import Select, { SingleValue } from 'react-select';
import { ReactSelectOption } from '../../xnp/common/types';
import { yettelNoBorder } from '../../checkout/common/select-styles/selectCustomStyles';
import { Form } from 'react-bootstrap';
import Button from '../../../components/buttons/Button';
import { getActiveLanguage, getLabelsByLanguage } from '../../../../utilites/util';
import Topup from '../../../../constants/TopupTypes';
import { getItemFromSessionStorage } from '../../../../utilites/localStorage';
import { useSelector } from 'react-redux';
import { DEFAULT_PREFIX, internetTopup, mobilePrefixes, mobileTopup } from './constants';
import { getInformationValidationSchema } from './validation-schemas';
import { TopupFirstStepFields } from './types';
import HttpRequest from '../../../../services/HttpRequest';

type Props = {
  activeTopupType: string;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setErrors: Dispatch<SetStateAction<string[]>>;
  handleStepChange: (stepNumber: number) => void;
};

const TopupData: React.FC<Props> = ({ activeTopupType, setLoading, setErrors, handleStepChange }) => {
  const language = getActiveLanguage();
  const labels = getLabelsByLanguage();

  const user = useSelector((state: any) => state.session.user);

  const amounts = activeTopupType !== Topup.INTERNET ? mobileTopup : internetTopup;
  const amountInitialValue = activeTopupType !== Topup.INTERNET ? mobileTopup[0].value : internetTopup[0].value;

  const topupData = getItemFromSessionStorage('topupData', '{}');

  let initialValues = {
    topupType: activeTopupType,
    email: user.email ? user.email : '',
    prefix1: DEFAULT_PREFIX,
    prefix2: DEFAULT_PREFIX,
    msisdn1: '',
    msisdn2: '',
    amount: amountInitialValue,
    checked: false,
  };

  if (topupData) {
    initialValues = {
      topupType: topupData.topupType,
      email: topupData.email || user.email || '',
      prefix1: topupData?.prefix1 ?? DEFAULT_PREFIX,
      prefix2: topupData?.prefix2 ?? DEFAULT_PREFIX,
      msisdn1: topupData?.msisdn1 ?? '',
      msisdn2: topupData?.msisdn2 ?? '',
      amount: topupData?.amount ?? amountInitialValue,
      checked: topupData?.checked ?? false,
    };
  }

  const formik = useFormik<TopupFirstStepFields>({
    initialValues: initialValues,
    onSubmit: (values: Partial<TopupFirstStepFields>) => {
      handleFormSubmit(values);
    },
    validationSchema: getInformationValidationSchema(language),
    validateOnChange: true,
  });

  const handleFormSubmit = (formData: Partial<TopupFirstStepFields>) => {
    setErrors([]);
    setLoading(true);

    sessionStorage.setItem('topupData', JSON.stringify(formData));

    const submitData = {
      topupType: formData.topupType === Topup.STANDARD ? 'phone' : 'internet',
      email: formData.email,
      msisdn: `${formData.prefix1}${formData.msisdn1}`,
      retypedMsisdn: `${formData.prefix2}${formData.msisdn2}`,
      amount: Number(formData.amount),
    };

    HttpRequest.request({
      method: 'POST',
      url: '/topup/msisdn',
      data: submitData,
      headers: { 'lang': language }
    })
      .then((_) => {
        handleStepChange(2);
      })
      .catch((error) => {
        setErrors([error.message]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (formik && formik.values.topupType !== activeTopupType) {
      const amountValue = activeTopupType !== Topup.INTERNET ? mobileTopup[0].value : internetTopup[0].value;

      formik.setFieldValue('topupType', activeTopupType);
      formik.setFieldValue('amount', amountValue);
    }
  }, [activeTopupType]);

  return (
    <div className="ytope-form active">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          formik.submitForm();
        }}
      >
        <input type="hidden" value={formik.values.topupType} />
        <div className="pt-4">
          <Label>Email*</Label>
          <input
            type="text"
            placeholder="E-mail"
            name="email"
            value={formik.values.email}
            className={classNames('ytope-form__email', shouldHaveFormikError(formik, 'email') && ERROR_CLASS)}
            onChange={formik.handleChange}
          />
          {shouldHaveFormikError(formik, 'email') && <div className="topup-error">{formik.errors.email}</div>}
        </div>

        <div className="pt-4">
          <Label>{labels.topupAmount}</Label>
          <div className="ytope-form__amount-list ">
            {amounts.map((option: ReactSelectOption, index: number) => {
              return (
                <div
                  key={index}
                  className="width-170 pt-2 pb-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    formik.setFieldValue('amount', option.value);
                  }}
                >
                  <label className="form-label cursorPointer" htmlFor={option.label}>
                    <input
                      className="checkbox me-4"
                      id={option.value}
                      type="radio"
                      name="contract"
                      checked={option.value === formik.values.amount}
                    />
                    {option.label}
                  </label>
                </div>
              );
            })}
          </div>
        </div>

        <div className="pt-4">
          <Label>{labels.topupMsisdn1}</Label>
          <div className="ytope-form__phone-number">
            <Select
              id="selectPrefix1"
              className={'dd-container my-auto'}
              value={{
                value: formik.values.prefix1,
                label: formik.values.prefix1,
              }}
              options={mobilePrefixes}
              isDisabled={false}
              onChange={(value: SingleValue<ReactSelectOption>) => {
                //@ts-ignore
                formik.setFieldValue('prefix1', value.value);
              }}
              styles={yettelNoBorder}
              isSearchable={false}
            />
            <input
              type="text"
              placeholder={labels.enterNumber}
              name="msisdn1"
              value={formik.values.msisdn1}
              className={classNames(shouldHaveFormikError(formik, 'msisdn1') && ERROR_CLASS)}
              onChange={formik.handleChange}
            />
          </div>
          {shouldHaveFormikError(formik, 'msisdn1') && <div className="topup-error">{formik.errors.msisdn1}</div>}
        </div>

        <div className="pt-4">
          <Label>{labels.topupMsisdn1Retyped}</Label>
          <div className="ytope-form__phone-number">
            <Select
              id="selectPrefix2"
              className={'dd-container my-auto'}
              value={{
                value: formik.values.prefix2,
                label: formik.values.prefix2,
              }}
              options={mobilePrefixes}
              isDisabled={false}
              onChange={(value: SingleValue<ReactSelectOption>) => {
                //@ts-ignore
                formik.setFieldValue('prefix2', value.value);
              }}
              styles={yettelNoBorder}
              isSearchable={false}
            />
            <input
              type="text"
              placeholder={labels.enterNumber}
              name="msisdn2"
              value={formik.values.msisdn2}
              className={classNames(shouldHaveFormikError(formik, 'msisdn2') && ERROR_CLASS)}
              onChange={formik.handleChange}
            />
          </div>
          {shouldHaveFormikError(formik, 'prefix2') && <div className="topup-error">{formik.errors.prefix2}</div>}
          {shouldHaveFormikError(formik, 'msisdn2') && <div className="topup-error">{formik.errors.msisdn2}</div>}
        </div>

        <div className="pt-4">
          <Form.Check
            type="checkbox"
            id="terms-checkbox"
            checked={formik.values.checked}
            onChange={(e) => {
              formik.setFieldValue('checked', e.target.checked);
            }}
            label={<span dangerouslySetInnerHTML={{ __html: labels.acceptTermsTopup }} />}
            data-testid="checkbox"
            className="yettel-font"
          />
        </div>

        <div className="pt-4 pb-4">
          <section className="d-flex justify-content-center">
            <Button
              type="submit"
              disabled={false}
              style={classNames(!formik.values.checked && 'btn-blue-yettel--disabled', 'btn-blue-yettel')}
            >
              {labels.continue}
            </Button>
          </section>
        </div>
      </form>
    </div>
  );
};

export default TopupData;
